import { useState, type ReactElement } from 'react';
import { Button, Group, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { AddressElement, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import type { BillingViewModel } from './BillingViewModel';

/**
 * Stripe card-capture form using the **Payment Element** + **Address Element** —
 * must be rendered inside a Stripe `<Elements>` provider initialized with a
 * SetupIntent client secret. Confirms the SetupIntent off-session (no redirect for
 * cards) and saves the resulting payment method as the tenant default; the address
 * flows to Stripe for tax. Shared by the billing settings page and the first-run
 * onboarding payment step.
 */
export function CardForm({
    vm,
    onDone,
    saveLabel = 'Save card',
    onCancel,
}: {
    vm: BillingViewModel;
    onDone: () => void;
    saveLabel?: string;
    onCancel?: () => void;
}): ReactElement {
    const stripe = useStripe();
    const elements = useElements();
    const [busy, setBusy] = useState(false);

    const submit = async (): Promise<void> => {
        if (!stripe || !elements) return;
        setBusy(true);
        try {
            const { error, setupIntent } = await stripe.confirmSetup({
                elements,
                confirmParams: { return_url: window.location.href },
                redirect: 'if_required',
            });
            if (error) {
                notifications.show({ color: 'red', title: 'Card not saved', message: error.message ?? 'Card setup failed' });
                return;
            }
            const pmId = setupIntent?.payment_method;
            if (typeof pmId !== 'string') {
                notifications.show({ color: 'red', message: 'No payment method returned' });
                return;
            }
            await vm.saveCard(pmId);
            notifications.show({ color: 'green', message: 'Card saved' });
            onDone();
        } catch (e) {
            notifications.show({ color: 'red', title: 'Card not saved', message: e instanceof Error ? e.message : 'Unexpected error' });
        } finally {
            setBusy(false);
        }
    };

    return (
        <Stack gap="md" maw={480}>
            <PaymentElement />
            <AddressElement options={{ mode: 'billing' }} />
            <Group>
                <Button loading={busy} onClick={() => void submit()}>{saveLabel}</Button>
                {onCancel && <Button variant="subtle" color="gray" onClick={onCancel} disabled={busy}>Cancel</Button>}
            </Group>
            <Text size="xs" c="dimmed">Card details go directly to Stripe — they never touch our servers.</Text>
        </Stack>
    );
}
